import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { titanApi } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  BookOpen,
  Clock,
  Target,
  Loader2,
  ShieldCheck,
  ArchiveX,
} from 'lucide-react';
import { Separator } from '../ui/separator';
import AssetSessionPanel from '../titan/AssetSessionPanel';

// ─────────────────────────────────────────────────────────────────────────────
// Types — schema v2 structured learnings + monthly principles
// ─────────────────────────────────────────────────────────────────────────────

interface OutcomeEntry {
  date: string;
  traded: boolean;
  pnl?: number;
  baseline_pnl?: number;
  trades?: number;
  reason?: string;
}

interface StructuredLearning {
  id: string;
  text: string;
  pattern_ids?: string[];
  asset_classes?: string[];
  regime_tags?: string[];
  category?: string;
  severity?: 'informational' | 'medium' | 'critical';
  evidence?: Record<string, unknown>;
  outcome_history?: OutcomeEntry[];
  status?: 'active' | 'deprecated';
  deprecated_at?: string;
}

interface WarRoomLearningsDoc {
  date: string;
  overall_performance?: string;
  asset_class_notes?: Record<string, string>;
  execution_summary?: {
    total_execution_events?: number;
    successful_execution_events?: number;
    failed_execution_events?: number;
    by_asset_class?: Record<string, number>;
  };
  learnings?: StructuredLearning[];
  // Backwards-compat — schema v1 docs only had this:
  actionable_learnings?: string[];
  day_regime_tags?: string[];
  pattern_ids_traded?: string[];
}

interface MonthlyPrinciple {
  id: string;
  text: string;
  regime_tags?: string[];
  pattern_ids?: string[];
  category?: string;
  confidence?: 'low' | 'medium' | 'high';
  source_learning_ids?: string[];
  source_dates?: string[];
}

interface MonthlyPrinciplesDoc {
  month: string;
  principles?: MonthlyPrinciple[];
  source_doc_count?: number;
  source_learning_count?: number;
  overall_themes?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Visual helpers
// ─────────────────────────────────────────────────────────────────────────────

const severityClass = (sev?: string) => {
  switch (sev) {
    case 'critical':
      return 'bg-rose-500/10 text-rose-500 border-rose-500/30';
    case 'medium':
      return 'bg-amber-500/10 text-amber-600 border-amber-500/30';
    default:
      return 'bg-slate-500/10 text-slate-500 border-slate-500/30';
  }
};

const confidenceClass = (c?: string) => {
  switch (c) {
    case 'high':
      return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30';
    case 'medium':
      return 'bg-amber-500/10 text-amber-600 border-amber-500/30';
    default:
      return 'bg-slate-500/10 text-slate-500 border-slate-500/30';
  }
};

const summarizeOutcomes = (history?: OutcomeEntry[]) => {
  const traded = (history || []).filter(e => e.traded);
  if (traded.length === 0) return null;
  const aggPnl = traded.reduce((s, e) => s + (e.pnl ?? 0), 0);
  const aggBaseline = traded.reduce((s, e) => s + (e.baseline_pnl ?? 0), 0);
  return { count: traded.length, aggPnl, aggBaseline };
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const LearningCard: React.FC<{ learning: StructuredLearning }> = ({ learning }) => {
  const isDeprecated = learning.status === 'deprecated';
  const outcomes = summarizeOutcomes(learning.outcome_history);

  return (
    <div
      className={`p-3 rounded-md border ${
        isDeprecated
          ? 'bg-muted/20 border-border/40 opacity-60'
          : 'bg-secondary/30 border-border/50'
      }`}
    >
      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
        {learning.severity && (
          <Badge variant="outline" className={`text-[10px] ${severityClass(learning.severity)}`}>
            {learning.severity.toUpperCase()}
          </Badge>
        )}
        {learning.category && (
          <Badge variant="outline" className="text-[10px]">
            {learning.category}
          </Badge>
        )}
        {(learning.pattern_ids ?? []).map(pid => (
          <Badge key={pid} variant="secondary" className="text-[10px] font-mono">
            {pid}
          </Badge>
        ))}
        {(learning.regime_tags ?? []).map(tag => (
          <Badge key={tag} variant="outline" className="text-[10px]">
            {tag}
          </Badge>
        ))}
        {isDeprecated && (
          <Badge
            variant="outline"
            className="text-[10px] bg-rose-500/10 text-rose-500 border-rose-500/30 ml-auto"
          >
            <ArchiveX className="w-3 h-3 mr-0.5" /> deprecated
          </Badge>
        )}
      </div>

      <p className="text-sm text-muted-foreground leading-snug">{learning.text}</p>

      {outcomes && (
        <div className="mt-2 pt-2 border-t border-border/30 flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" />
            {outcomes.count} follow-up{outcomes.count !== 1 ? 's' : ''}
          </span>
          <span
            className={`font-mono ${
              outcomes.aggPnl >= 0 ? 'text-emerald-500' : 'text-rose-500'
            }`}
          >
            {outcomes.aggPnl >= 0 ? '+' : ''}${outcomes.aggPnl.toFixed(0)}
          </span>
          <span className="text-muted-foreground/70">
            vs baseline {outcomes.aggBaseline >= 0 ? '+' : ''}$
            {outcomes.aggBaseline.toFixed(0)}
          </span>
        </div>
      )}
    </div>
  );
};

const PrincipleCard: React.FC<{ principle: MonthlyPrinciple }> = ({ principle }) => (
  <div className="p-3 rounded-md border bg-primary/5 border-primary/20">
    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
      {principle.confidence && (
        <Badge
          variant="outline"
          className={`text-[10px] ${confidenceClass(principle.confidence)}`}
        >
          {principle.confidence.toUpperCase()}
        </Badge>
      )}
      {principle.category && (
        <Badge variant="outline" className="text-[10px]">
          {principle.category}
        </Badge>
      )}
      {(principle.pattern_ids ?? []).map(pid => (
        <Badge key={pid} variant="secondary" className="text-[10px] font-mono">
          {pid}
        </Badge>
      ))}
      {(principle.regime_tags ?? []).map(tag => (
        <Badge key={tag} variant="outline" className="text-[10px]">
          {tag}
        </Badge>
      ))}
      {(principle.source_learning_ids?.length ?? 0) > 0 && (
        <span className="text-[10px] text-muted-foreground ml-auto">
          {principle.source_learning_ids!.length} sources
        </span>
      )}
    </div>
    <p className="text-sm text-muted-foreground leading-snug">{principle.text}</p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main dashboard
// ─────────────────────────────────────────────────────────────────────────────

export const WarRoomDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const [strategy, setStrategy] = useState<any>(null);
  const [learnings, setLearnings] = useState<WarRoomLearningsDoc[]>([]);
  const [principles, setPrinciples] = useState<MonthlyPrinciplesDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWarRoomData = async () => {
      if (!currentUser?.uid) return;

      try {
        // Served from PostgreSQL via the backend API (no Firestore dependency).
        const data = await titanApi.warRoomDashboard();
        if (data.strategy) setStrategy(data.strategy);
        if (Array.isArray(data.learnings) && data.learnings.length > 0) {
          setLearnings(data.learnings as WarRoomLearningsDoc[]);
        }
        if (Array.isArray(data.principles) && data.principles.length > 0) {
          setPrinciples(data.principles as MonthlyPrinciplesDoc[]);
        }
      } catch (err) {
        console.error('Failed to fetch War Room data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWarRoomData();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin mb-4" />
        <p>Loading War Room Intelligence...</p>
      </div>
    );
  }

  if (!strategy) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground text-center">
        <Target className="w-12 h-12 mb-4 opacity-50" />
        <h3 className="text-lg font-medium text-foreground mb-2">Awaiting First Strategy</h3>
        <p className="max-w-md">
          The autonomous War Room has not generated a daily strategy yet. Strategies are
          generated automatically at the end of the trading day.
        </p>
      </div>
    );
  }

  const getBiasIcon = (bias: string) => {
    if (bias?.toLowerCase() === 'bullish') return <TrendingUp className="w-5 h-5 text-emerald-500" />;
    if (bias?.toLowerCase() === 'bearish') return <TrendingDown className="w-5 h-5 text-rose-500" />;
    return <Minus className="w-5 h-5 text-slate-500" />;
  };

  const getBiasColor = (bias: string) => {
    if (bias?.toLowerCase() === 'bullish') return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
    if (bias?.toLowerCase() === 'bearish') return 'bg-rose-500/10 text-rose-500 border-rose-500/20';
    return 'bg-slate-500/10 text-slate-500 border-slate-500/20';
  };

  const getRiskColor = (risk: string) => {
    switch (risk?.toLowerCase()) {
      case 'extreme': return 'text-rose-600';
      case 'elevated': return 'text-rose-400';
      case 'normal':  return 'text-amber-500';
      case 'low':     return 'text-emerald-500';
      default:        return 'text-amber-500';
    }
  };

  const latestPrinciplesDoc = principles[0];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Daily Strategy Briefing</h2>
          <p className="text-muted-foreground">Autonomous War Room consensus for {strategy.date}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {(strategy.regime_tags ?? []).map((t: string) => (
            <Badge key={t} variant="outline" className="text-[10px]">
              {t}
            </Badge>
          ))}
          <Badge variant="outline" className="flex items-center gap-1.5 py-1">
            <Clock className="w-3 h-3" />
            Generated {new Date(strategy.timestamp).toLocaleTimeString()}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>Strategic Rationale</CardTitle>
                <CardDescription>AI Committee Synthesis</CardDescription>
              </div>
              <Badge className={getBiasColor(strategy.overall_bias)}>
                <span className="flex items-center gap-1.5 capitalize">
                  {getBiasIcon(strategy.overall_bias)}
                  {strategy.overall_bias} Bias
                </span>
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm leading-relaxed text-muted-foreground">{strategy.rationale}</p>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Key Themes
                </h4>
                <ul className="space-y-1 mb-4">
                  {strategy.key_themes?.map((theme: string, i: number) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                      <span>{theme}</span>
                    </li>
                  ))}
                </ul>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Recommended Bots
                </h4>
                <ul className="space-y-1">
                  {strategy.bot_strategies?.map((bot: any, i: number) => {
                    const label = typeof bot === 'string' ? bot : (bot?.name || bot?.template_id || 'Bot');
                    const sub = typeof bot === 'object' ? bot?.rationale : null;
                    const ac = typeof bot === 'object' ? bot?.asset_class : null;
                    return (
                      <li key={`bot-${i}`} className="text-sm flex items-start gap-2 text-primary">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                        <div>
                          <span className="font-medium">{label}</span>
                          {ac && <span className="ml-1 text-xs text-muted-foreground capitalize">({ac})</span>}
                          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
                        </div>
                      </li>
                    );
                  })}
                  {(!strategy.bot_strategies || strategy.bot_strategies.length === 0) && (
                    <li className="text-sm text-muted-foreground">No specific bot changes</li>
                  )}
                </ul>

                {(strategy.strategy_decisions ?? []).length > 0 && (
                  <>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-4 mb-2">
                      Decisions cited from history
                    </h4>
                    <ul className="space-y-1.5">
                      {strategy.strategy_decisions.map((d: any, i: number) => (
                        <li
                          key={i}
                          className="text-xs text-muted-foreground p-2 bg-muted/30 rounded border border-border/40"
                        >
                          <p>{d.decision}</p>
                          {(d.drew_from_learning_ids?.length ?? 0) > 0 && (
                            <p className="text-[10px] text-muted-foreground/70 mt-1 font-mono">
                              cites: {d.drew_from_learning_ids.join(', ')}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Risk Environment
                </h4>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className={`w-4 h-4 ${getRiskColor(strategy.risk_level)}`} />
                  <span className="text-sm font-medium capitalize">{strategy.risk_level}</span>
                </div>
                {strategy.position_size_modifier != null && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                      Size Modifier
                    </h4>
                    <span
                      className={`text-sm font-mono font-bold ${
                        strategy.position_size_modifier >= 1 ? 'text-emerald-500' : 'text-rose-400'
                      }`}
                    >
                      {strategy.position_size_modifier.toFixed(2)}×
                    </span>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Applied to all position sizes today
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Asset Allocation</CardTitle>
            <CardDescription>Recommended exposure</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(strategy.asset_allocation || {}).map(([asset, weight]: [string, any]) => (
                <div key={asset}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize font-medium">{asset}</span>
                    <span className="text-muted-foreground">{(weight * 100).toFixed(0)}%</span>
                  </div>
                  <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${weight * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live session goal progress — shows after War Room sets targets */}
      <div>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <Target className="w-4 h-4 text-primary" />
          Today's Session Goals
          {strategy.daily_target_pct != null && (
            <span className="text-xs text-muted-foreground font-normal">
              ({strategy.daily_target_pct.toFixed(1)}% portfolio target)
            </span>
          )}
        </h3>
        <AssetSessionPanel compact />
      </div>

      {/* Phase 4 — durable monthly principles */}
      {latestPrinciplesDoc && (latestPrinciplesDoc.principles?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
              Durable Principles — {latestPrinciplesDoc.month}
            </CardTitle>
            <CardDescription>
              Distilled from {latestPrinciplesDoc.source_learning_count ?? 0} daily learnings
              across {latestPrinciplesDoc.source_doc_count ?? 0} sessions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(latestPrinciplesDoc.principles ?? []).map(p => (
              <PrincipleCard key={p.id} principle={p} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Phase 1+3 — structured daily learnings with outcome history */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Post-Day Actionable Learnings
          </CardTitle>
          <CardDescription>
            Historical lessons actively guiding today's strategy — verified against subsequent
            outcomes, deprecated when patterns underperform
          </CardDescription>
        </CardHeader>
        <CardContent>
          {learnings.length > 0 ? (
            <div className="space-y-6">
              {learnings.map((doc, idx) => {
                const structured = doc.learnings ?? [];
                const legacy = (!structured.length && doc.actionable_learnings) || [];
                return (
                  <div key={idx} className="space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h4 className="text-sm font-semibold">{doc.date}</h4>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {doc.execution_summary?.total_execution_events != null && (
                          <Badge variant="outline" className="text-[10px]">
                            executions: {doc.execution_summary.total_execution_events}
                          </Badge>
                        )}
                        {(doc.day_regime_tags ?? []).map(t => (
                          <Badge key={t} variant="outline" className="text-[10px]">
                            {t}
                          </Badge>
                        ))}
                        {doc.overall_performance && (
                          <span className="text-xs text-muted-foreground px-2 py-1 bg-secondary rounded-md">
                            {doc.overall_performance}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Asset-class notes */}
                    {doc.asset_class_notes && Object.keys(doc.asset_class_notes).length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(doc.asset_class_notes).map(([asset, note]) => (
                          <div
                            key={asset}
                            className="p-2 bg-muted/40 rounded-md border border-border/30"
                          >
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                              {asset}
                            </span>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                              {note}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Schema v2 — structured cards */}
                    {structured.length > 0 && (
                      <div className="space-y-2">
                        {structured.map(l => (
                          <LearningCard key={l.id} learning={l} />
                        ))}
                      </div>
                    )}

                    {/* Schema v1 fallback — legacy string list */}
                    {legacy.length > 0 && (
                      <ul className="space-y-2">
                        {legacy.map((item, i) => (
                          <li
                            key={i}
                            className="text-sm text-muted-foreground flex gap-2 p-2 bg-secondary/30 rounded-md border border-border/50"
                          >
                            <span className="text-primary mt-0.5">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}

                    {idx < learnings.length - 1 && <Separator className="mt-4" />}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No historical learnings recorded yet.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
